import { ReactNode } from "react";

/* Shared chrome for the four approved Aave entity intelligence modules
   (Governance / Security / Regulation / Layer Index Context).
   Visual authority: approved module frames, rendered in NewsLayer tokens. */

export const INNER_CARD = "bg-[#07070a] border border-[#1a1c2b] rounded-[14px]";

export const relTime = (iso?: string | null) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 90) return "just now";
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

export const isDelayed = (iso?: string | null) =>
  !iso || Date.now() - new Date(iso).getTime() > 24 * 3600 * 1000;

export const fmtDate = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export const UpdatedStamp = ({ iso, testid }: { iso?: string | null; testid?: string }) => (
  <span className="nl-mono text-[8px] md:text-[9px] leading-[12px] font-medium uppercase text-[color:var(--nl-text-faint)]"
    data-testid={testid}>
    {isDelayed(iso) ? "DATA DELAYED" : `UPDATED ${relTime(iso).toUpperCase()}`}
  </span>
);

export const StatusBadge = ({ label, color, testid }: { label: string; color: string; testid?: string }) => (
  <span data-testid={testid}
    className="nl-mono inline-block text-[8px] md:text-[9px] leading-[12px] font-semibold uppercase tracking-[0.06em] px-[8px] py-[3px] rounded-[6px] border whitespace-nowrap"
    style={{ color, borderColor: `${color}55`, background: `${color}0d` }}>
    {label}
  </span>
);

export const ModuleShell = ({ num, title, sub, right, children, footNote, footRight, testid }: {
  num: string; title: string; sub: string; right?: ReactNode; children: ReactNode;
  footNote?: ReactNode; footRight?: ReactNode; testid: string;
}) => (
  <section data-testid={testid}
    className="rounded-[20px] md:rounded-[24px] lg:rounded-[28px] bg-[#030405] border border-[#171a26] px-[16px] py-[18px] md:px-[24px] md:py-[24px] lg:px-[28px] lg:py-[26px]">
    <div className="flex flex-wrap items-start justify-between gap-x-[16px] gap-y-[14px]">
      <div className="flex items-start gap-[13px] min-w-0">
        <span aria-hidden
          className="nl-sg flex items-center justify-center shrink-0 h-[30px] w-[30px] md:h-[34px] md:w-[34px] rounded-[9px] text-[14px] md:text-[15px] font-semibold text-[#b79aff] bg-[#7a3dff]/[0.13] border border-[#7a3dff]/[0.42]">
          {num}
        </span>
        <div className="min-w-0">
          <h2 className="nl-sg text-[17px] leading-[22px] md:text-[21px] md:leading-[26px] font-semibold tracking-[0.03em] uppercase text-[#f6f7fc]">
            {title}
          </h2>
          <p className="nl-geist text-[11px] md:text-[12px] leading-[16px] text-[#9499ad] mt-[3px]">{sub}</p>
        </div>
      </div>
      {right && <div className="shrink-0">{right}</div>}
    </div>
    <div className="mt-[20px] md:mt-[24px]">{children}</div>
    {(footNote || footRight) && (
      <div className="flex flex-wrap items-center justify-between gap-x-[20px] gap-y-[8px] border-t border-[#171a26] mt-[22px] md:mt-[26px] pt-[14px]">
        <div className="nl-geist text-[10px] md:text-[11px] leading-[15px] text-[#9499ad] max-w-[640px]">{footNote}</div>
        <div className="flex items-center gap-[14px]">{footRight}</div>
      </div>
    )}
  </section>
);

export const ModuleDelayed = ({ num, title, sub, message, testid }: {
  num: string; title: string; sub: string; message: string; testid: string;
}) => (
  <ModuleShell num={num} title={title} sub={sub} testid={testid}>
    <p className="nl-geist text-[13px] leading-[19px] text-[#9499ad]" role="status">{message}</p>
  </ModuleShell>
);
